import React, { useEffect, useState } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, Box, Collapse, IconButton, Menu, MenuItem } from '@mui/material';
import { Chat, AdminPanelSettings, ExpandLess, ExpandMore, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Storage, Settings, History } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';

const drawerWidth = 240;

interface NavigationProps {
    drawerOpen: boolean;
    setDrawerOpen: (open: boolean) => void;
}

interface UserClaims {
    roles: string[];
}

const Navigation: React.FC<NavigationProps> = ({ drawerOpen, setDrawerOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { instance, accounts } = useMsal();
    const [adminOpen, setAdminOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [userRoles, setUserRoles] = useState<string[]>([]);

    // Get the first account (current user)
    const user = accounts && accounts.length > 0 ? accounts[0] : null;

    useEffect(() => {
        const extractUserRoles = async () => {
            if (accounts && accounts.length > 0) {
                try {
                    const account = accounts[0];
                    // Get the token claims
                    const tokenResult = await instance.acquireTokenSilent({
                        account: account,
                        scopes: ['User.Read']
                    });
                    
                    if (tokenResult.idToken) {
                        sessionStorage.setItem('mna_id_token', tokenResult.idToken);
                        const decodedToken = jwtDecode<UserClaims>(tokenResult.idToken);
                        const roles = decodedToken.roles || [];                       
                        setUserRoles(roles);
                    }
                } catch (error) {
                    console.error('Error extracting user roles:', error);
                    setUserRoles([]);
                }
            }
        };

        extractUserRoles().catch(error => {
            console.error('Error in useEffect:', error);
            setUserRoles([]);
        });
    }, [accounts, instance]);

    const menuItems = [
        { text: 'Chat', icon: <Chat />, path: '/chat' },
        { text: 'Batch History', icon: <History />, path: '/batch-history' },
        ...(userRoles.includes('Chat.Admin') ? [{
            text: 'Admin', 
            icon: <AdminPanelSettings />,
            subMenus: [
                { text: 'Data Configuration', path: '/admin/data-source', icon: <Storage /> },
                { text: 'Prompt Configuration', path: '/admin/prompt', icon: <Settings /> },
                { text: 'Batch Prompt Configuration', path: '/admin/batch-prompt', icon: <Settings /> },
            ]
        }] : [])
    ];

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        {/* Danaher Logo on the left */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                            <img
                                src="/Danaher.png" // <-- Make sure this matches the actual path in your public folder
                                alt="Danaher Logo"
                                style={{
                                    width: 'auto',
                                    height: '40px',
                                    padding: '0px',
                                }}
                            />
                        </Box>
                        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                            M&AI <Typography component="span" variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>Powered by Brain</Typography>
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {user && (
                            <>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, cursor: 'pointer' }}
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                >
                                    {user.name || user.username}
                                </Typography>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => setAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{ sx: { mt: 1 } }}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            setAnchorEl(null);
                                            instance.logoutRedirect();
                                        }}
                                    >
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                open={drawerOpen}
                sx={{
                    width: drawerOpen ? drawerWidth : 56,
                    flexShrink: 0,
                    transition: 'width 0.3s',
                    '& .MuiDrawer-paper': {
                        width: drawerOpen ? drawerWidth : 56,
                        boxSizing: 'border-box',
                        marginTop: '64px',
                        transition: 'width 0.3s',
                        overflowX: 'hidden',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: drawerOpen ? 'flex-end' : 'center', alignItems: 'center', height: 56 }}>
                    <IconButton
                        color="inherit"
                        aria-label={drawerOpen ? 'Collapse drawer' : 'Expand drawer'}
                        onClick={() => setDrawerOpen(!drawerOpen)}
                        size="large"
                    >
                        {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </Box>
                <List>
                    {menuItems.map((item) => (
                        item.text === 'Admin' ? (
                            <React.Fragment key={item.text}>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => setAdminOpen((prev) => !prev)}>
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        {drawerOpen && <ListItemText primary={item.text} />}
                                        {drawerOpen && (adminOpen ? <ExpandLess /> : <ExpandMore />)}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={adminOpen && drawerOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.subMenus && item.subMenus.map((sub) => (
                                            <ListItem key={sub.text} disablePadding sx={{ pl: 4 }}>
                                                <ListItemButton
                                                    selected={location.pathname === sub.path}
                                                    onClick={() => navigate(sub.path)}
                                                >
                                                    {drawerOpen && (
                                                        <>
                                                            <ListItemIcon>{sub.icon}</ListItemIcon>
                                                            <ListItemText primary={sub.text} />
                                                        </>
                                                    )}
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ) : (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton
                                    selected={location.pathname === item.path}
                                    onClick={() => item.path && navigate(item.path)}
                                >
                                    <ListItemIcon>{item.icon}</ListItemIcon>
                                    {drawerOpen && <ListItemText primary={item.text} />}
                                </ListItemButton>
                            </ListItem>
                        )
                    ))}
                </List>
            </Drawer>
        </Box>
    );
};

export default Navigation;
